import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "c-leads-form__media"
}
const _hoisted_2 = {
  key: 0,
  class: "c-leads-form__success"
}
const _hoisted_3 = {
  key: 1,
  class: "c-leads-form__form-column"
}
const _hoisted_4 = { class: "c-leads-form__side" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "c-leads-form__captcha" }
const _hoisted_7 = ["src"]
const _hoisted_8 = {
  key: 0,
  class: "c-leads-form__captcha-error"
}
const _hoisted_9 = {
  key: 2,
  class: "c-leads-form__right"
}

import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import utils from '@/utils';
import {
  LeadsFormComponentItem,
  LeadsFormData,
  LeadsFormProps,
} from '@/types/components/leads-form';
import DynamicForm from '@/form/DynamicForm.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import { LoadingProvider, ToastProvider } from '@/types/layout';
import { DynamicFormRef } from '@/types/form/dynamic-form';
import api from '@/api';
import componentFactory from '@/temp/componentFactory';
import {
  applyDynamicFormAutofill,
  isCountryField,
  isStateField,
  updateRegionalFieldsByCountry,
} from '@/utils/dynamic-form-autofill';
import { getOnlineMessageTracking } from '@/utils/online-message';
import { omitDynamicFormCheckboxValues } from '@/utils/dynamic-form-draft';

const FORM_DRAFT_STORAGE_PREFIX = 'onlineMessageFormDraft';

export default /*@__PURE__*/_defineComponent({
  __name: 'LeadsForm',
  props: {
    page: {},
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const loading = inject<LoadingProvider>('loading')!;
const toast = inject<ToastProvider>('toast')!;
const formRef = ref<DynamicFormRef>();
const captchaValue = ref('');
const captchaError = ref('');
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
const isSubmitted = ref(false);
const submitting = ref(false);
const currentCountryValue = ref('');
const formDraftStorageKey = ref('');
const isRestoringFormDraft = ref(false);
let formInitializationId = 0;
const rightComponents = computed(() =>
  (props.fields?.rightComponents ?? []).map((componentData) => ({
    ...componentData,
    componentName: getComponentName(componentData),
  }))
);
const rootClass = computed(() => {
  const result = utils.overall.baseComClass(props.fields, 'c-leads-form');
  const [desktopDirection] = utils.sp.getValByFields(props.fields, 'desktopDirection');
  console.log('desktopLayout', desktopDirection);
  if (!desktopDirection || ['row', 'row-reverse'].includes(desktopDirection)) {
    result.push('row');
  }
  return result;
});
const rootStyle = computed(() => {
  const result = utils.overall.baseComStyle(props.fields);
  const [desktopDirection] = utils.sp.getValByFields(props.fields, 'desktopDirection');
  if (desktopDirection) {
    result['--flex-direction'] = desktopDirection;
    if (['column', 'column-reverse'].includes(desktopDirection)) {
      result['--align'] = 'left';
    }
  }
  return result;
});
const normalizedBackMedia = computed(() => {
  if (!props.fields?.backMedia?.fields) return props.fields?.backMedia;

  const normalizeImageField = (imageField: any) => {
    const src = imageField?.value?.src;
    if (
      !src ||
      (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('//'))
    ) {
      return imageField;
    }

    const url = new URL(src.startsWith('//') ? `https:${src}` : src);
    return {
      ...imageField,
      value: {
        ...imageField.value,
        src: `${url.pathname}${url.search}${url.hash}`,
      },
    };
  };

  return {
    ...props.fields.backMedia,
    fields: {
      ...props.fields.backMedia.fields,
      desktopImage: normalizeImageField(props.fields.backMedia.fields.desktopImage),
      mobileImage: normalizeImageField(props.fields.backMedia.fields.mobileImage),
    },
  };
});
function getComponentName(componentData?: LeadsFormComponentItem) {
  return `${
    componentData?.componentName || componentData?.templateName || componentData?.template || ''
  }`.replace(/\s+/g, '');
}
function resolveDynamicComponent(componentData?: LeadsFormComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}
function getDynamicComponentProps(componentData: LeadsFormComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
  };
}
const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.codeToken || data.code_token || '';
  captchaImage.value = data.src || '';
};

const getCookie = (name: string) => {
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : '';
};

const getSubmitHeaders = () => {
  const session = getCookie('GSAccessSession');
  return {
    Referer: window.location.href,
    ...(session ? { GSAccessSession: session } : {}),
  };
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};
const onCaptchaInput = () => {
  captchaError.value = '';
};
const getHomeUrl = () => {
  const { pathname, origin } = window.location;
  const langMatch = pathname.match(/^\/([a-z]{2}-[A-Z]{2})(?:\/|$)/);
  return `${origin}${langMatch ? `/${langMatch[1]}` : ''}/`;
};
const goHome = () => {
  window.location.href = getHomeUrl();
};
const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }
  captchaError.value = 'Please enter the security code shown on the right.';
  return false;
};
const getCurrentCountryValue = () => {
  const values = formRef.value?.getValues?.() || {};
  return (
    values.country || values.countryRegion || values.countryCode || values.country_region || ''
  );
};

const getRegionalDraftValues = (values: Record<string, any>) => ({
  country:
    values.country || values.countryRegion || values.countryCode || values.country_region || '',
  state: values.state || values.State || '',
});

const persistFormDraft = () => {
  if (
    isSubmitted.value ||
    isRestoringFormDraft.value ||
    !formDraftStorageKey.value ||
    !formRef.value
  ) {
    return;
  }

  try {
    const draftValues = omitDynamicFormCheckboxValues(
      props.fields?.form,
      formRef.value.getValues()
    );
    window.localStorage.setItem(formDraftStorageKey.value, JSON.stringify(draftValues));
  } catch {
    // Ignore unavailable storage and values that cannot be serialized.
  }
};

const restoreFormDraft = () => {
  if (!formDraftStorageKey.value || !formRef.value) return;

  try {
    const storedDraft = window.localStorage.getItem(formDraftStorageKey.value);
    const values = storedDraft ? JSON.parse(storedDraft) : null;
    if (!values || typeof values !== 'object' || Array.isArray(values)) return;
    const draftValues = omitDynamicFormCheckboxValues(props.fields?.form, values);

    // Rewrite old drafts so previously cached checkbox values are removed immediately.
    window.localStorage.setItem(formDraftStorageKey.value, JSON.stringify(draftValues));

    formRef.value.updateFields(
      Object.entries(draftValues).reduce<Record<string, { value: any }>>(
        (updates, [field, value]) => {
          updates[field] = { value };
          return updates;
        },
        {}
      )
    );
    const regionalDraft = getRegionalDraftValues(draftValues);
    currentCountryValue.value = regionalDraft.country;
    updateRegionalFieldsByCountry(formRef.value, regionalDraft.country, regionalDraft.state);
  } catch {
    // Ignore malformed drafts so users can still use the form.
  }
};

const clearFormDraft = () => {
  if (!formDraftStorageKey.value) return;
  window.localStorage.removeItem(formDraftStorageKey.value);
};

const onDocumentInput = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest('.c-leads-form')) {
    void nextTick().then(persistFormDraft);
  }
};

const initializeForm = async () => {
  const initializationId = ++formInitializationId;
  persistFormDraft();
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  formDraftStorageKey.value = `${FORM_DRAFT_STORAGE_PREFIX}:${pathname}`;
  isRestoringFormDraft.value = true;
  await nextTick();
  await applyDynamicFormAutofill(formRef.value);
  if (initializationId !== formInitializationId) return;
  restoreFormDraft();
  currentCountryValue.value = getCurrentCountryValue();
  isRestoringFormDraft.value = false;
};

const onFormChange = (_fieldType: string, fieldName: string, value: string) => {
  if (isCountryField(fieldName)) {
    currentCountryValue.value = value || '';
    updateRegionalFieldsByCountry(formRef.value, value, undefined, true);
  } else if (isStateField(fieldName)) {
    updateRegionalFieldsByCountry(
      formRef.value,
      currentCountryValue.value || getCurrentCountryValue(),
      value
    );
  }

  void nextTick().then(persistFormDraft);
};

const escapeHtml = (value: unknown) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatEmailBody = (formData: Record<string, any>) =>
  Object.entries(formData)
    .filter(([fieldName]) => fieldName !== 'externalData')
    .map(([fieldName, value]) => {
      const displayValue = Array.isArray(value) ? value.join(', ') : value;
      return `<p><strong>${escapeHtml(fieldName)}</strong>: ${escapeHtml(displayValue)}</p> <br />`;
    })
    .join('');

const onSubmit = async () => {
  if (submitting.value || !formRef.value) return;
  const formRes = await formRef.value.validate<LeadsFormData>();
  if (!formRes.valid) {
    formRef.value.focusFirstInvalid?.();
    return;
  }
  if (!formRes.data) return;
  if (!validateCaptcha()) return;

  submitting.value = true;
  loading.show();
  try {
    const formType = props.fields?.formType;
    const hasFormType = Boolean(formType?.fields);
    const [, error] = hasFormType
      ? await api.contact.sendCustomerEmail(
          null,
          {
            subject: formType?.fields?.text?.value || '',
            body: formatEmailBody(formRes.data),
            type: formType?.fields?.phrase?.value || '',
          },
          { headers: getSubmitHeaders() }
        )
      : await api.downloadResource.submitOnlineMessage(
          null,
          {
            ...formRes.data,
            ...getOnlineMessageTracking(),
            validateCode: captchaValue.value.trim(),
            codeToken: codeToken.value,
          },
          { headers: getSubmitHeaders() }
        );

    if (error) {
      void toast.showError(getErrorMessage(error, 'Submission failed. Please try again.'));
      await refreshCaptcha();
      return;
    }

    isSubmitted.value = true;
    const successUrl = new URL(window.location.href);
    successUrl.search = '?success';
    window.history.replaceState(window.history.state, '', successUrl.toString());
    clearFormDraft();
    captchaValue.value = '';
    await refreshCaptcha();
  } finally {
    submitting.value = false;
    loading.hide();
  }
};
onMounted(() => {
  refreshCaptcha();
  void initializeForm();
  window.addEventListener('pagehide', persistFormDraft);
  window.addEventListener('beforeunload', persistFormDraft);
  document.addEventListener('input', onDocumentInput, true);
});

watch(
  () => props.fields?.form,
  () => {
    void initializeForm();
  }
);

onBeforeUnmount(() => {
  persistFormDraft();
  window.removeEventListener('pagehide', persistFormDraft);
  window.removeEventListener('beforeunload', persistFormDraft);
  document.removeEventListener('input', onDocumentInput, true);
});

return (_ctx: any,_cache: any) => {
  const _component_AdaptiveImage = _resolveComponent("AdaptiveImage")!
  const _component_SiteButton = _resolveComponent("SiteButton")!
  const _component_content_grid = _resolveComponent("content-grid")!

  return (__props.fields)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value)
      }, [
        (normalizedBackMedia.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_component_AdaptiveImage, {
                field: normalizedBackMedia.value,
                class: "c-leads-form__media-image",
                "background-position": "right bottom",
                "is-background": true,
                "lazy-load": false,
                "enable-scroll-detection": false,
                show: true,
                "show-image-redius": false
              }, null, 8, ["field"])
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_content_grid, {
          "desktop-default-grid": _unref(utils).gs.getGridRange(__props.fields.desktopGrid),
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              class: _normalizeClass(['c-leads-form__content', { 'has-right': !isSubmitted.value && rightComponents.value.length }])
            }, [
              (isSubmitted.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _cache[1] || (_cache[1] = _createElementVNode("div", {
                      class: "c-leads-form__success-icon",
                      "aria-hidden": "true"
                    }, [
                      _createElementVNode("svg", {
                        viewBox: "0 0 32 32",
                        focusable: "false"
                      }, [
                        _createElementVNode("path", { d: "M8.5 16.5 13.5 21.5 24 10.5" })
                      ])
                    ], -1)),
                    _createVNode(SiteRichText, {
                      field: __props.fields.successTitle,
                      class: "c-leads-form__success-title"
                    }, null, 8, ["field"]),
                    _createVNode(SiteRichText, {
                      field: __props.fields.successBody,
                      class: "c-leads-form__success-body"
                    }, null, 8, ["field"]),
                    _createElementVNode("button", {
                      class: "c-leads-form__success-button",
                      type: "button",
                      onClick: goHome
                    }, " Return To Homepage ")
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      (__props.fields?.title?.value)
                        ? (_openBlock(), _createElementBlock("h2", {
                            key: 0,
                            innerHTML: __props.fields?.title.value
                          }, null, 8, _hoisted_5))
                        : _createCommentVNode("", true),
                      _createVNode(SiteRichText, {
                        field: __props.fields.body,
                        class: "c-leads-form__body"
                      }, null, 8, ["field"]),
                      _createVNode(SiteRichText, {
                        field: __props.fields.tips,
                        class: "c-leads-form__tips"
                      }, null, 8, ["field"])
                    ]),
                    _createElementVNode("div", {
                      class: "c-leads-form__main",
                      onInput: persistFormDraft
                    }, [
                      _createVNode(DynamicForm, {
                        form: __props.fields.form,
                        ref_key: "formRef",
                        ref: formRef,
                        onChange: onFormChange
                      }, null, 8, ["form"]),
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", {
                          class: _normalizeClass(['c-leads-form__captcha-field', { 'has-error': Boolean(captchaError.value) }])
                        }, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((captchaValue).value = $event)),
                            type: "text",
                            inputmode: "text",
                            autocomplete: "off",
                            autocapitalize: "none",
                            spellcheck: "false",
                            placeholder: "security code on the right",
                            "aria-label": "Security code",
                            onInput: onCaptchaInput
                          }, null, 544), [
                            [_vModelText, captchaValue.value]
                          ])
                        ], 2),
                        _createElementVNode("img", {
                          class: _normalizeClass(["c-leads-form__captcha-image", { 'is-loading': captchaLoading.value }]),
                          src: captchaImage.value,
                          alt: "Security code",
                          role: "button",
                          onClick: refreshCaptcha
                        }, null, 10, _hoisted_7),
                        (captchaError.value)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(captchaError.value), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createVNode(_component_SiteButton, _mergeProps(__props.fields.submitButton, {
                        onClick: onSubmit,
                        class: "c-leads-form__button",
                        disabled: submitting.value,
                        loading: submitting.value
                      }), null, 16, ["disabled", "loading"])
                    ], 32)
                  ])),
              (!isSubmitted.value && rightComponents.value.length)
                ? (_openBlock(), _createElementBlock("aside", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rightComponents.value, (componentData, componentIndex) => {
                      return (_openBlock(), _createBlock(_resolveDynamicComponent(resolveDynamicComponent(componentData)), _mergeProps({ ref_for: true }, getDynamicComponentProps(componentData), {
                        key: componentData?.uid ?? componentData?.id ?? `right-component-${componentIndex}`
                      }), null, 16))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ], 2)
          ]),
          _: 1
        }, 8, ["desktop-default-grid"])
      ], 6))
    : _createCommentVNode("", true)
}
}

})